/**
 * Provider Management Tab
 * Phase 4: Training Provider Expansion Framework
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  useProvidersExtended, 
  useManageProvider, 
  useDryRunDiscovery,
  useProviderAuditLog 
} from '@/hooks/useProviderManagement';
import { useRunDiscoveryNow } from '@/hooks/useDiscoverySources';
import { AddEditProviderDialog } from './AddEditProviderDialog';
import { 
  LIFECYCLE_STATUS_LABELS, 
  TRUST_LEVEL_LABELS,
  VISIBILITY_LEVEL_LABELS,
  type TrainingProviderExtended 
} from '@/types/training';
import { 
  Building2, 
  Play, 
  Pause, 
  EyeOff, 
  Eye, 
  Ban, 
  CheckCircle,
  BookOpen,
  Search,
  AlertTriangle,
  History,
  Plus,
  Edit,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ADD_DIALOG_STORAGE_KEY = 'provider-mgmt-add-dialog-open';

export function ProviderManagementTab() {
  const { data: providers, isLoading } = useProvidersExtended();
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  
  // Persist add dialog open state in sessionStorage
  const [showAddDialog, setShowAddDialog] = useState(() => 
    sessionStorage.getItem(ADD_DIALOG_STORAGE_KEY) === 'true'
  );
  
  useEffect(() => {
    sessionStorage.setItem(ADD_DIALOG_STORAGE_KEY, String(showAddDialog));
  }, [showAddDialog]);

  if (isLoading) {
    return <div className="p-4 text-muted-foreground">Loading providers...</div>;
  }

  const selectedProvider = providers?.find(p => p.id === selectedProviderId);

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Provider Management</h2>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-1" /> Add Provider
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Provider List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Providers ({providers?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1 p-2">
                {providers?.map(provider => (
                  <ProviderListItem
                    key={provider.id}
                    provider={provider}
                    isSelected={provider.id === selectedProviderId}
                    onSelect={() => setSelectedProviderId(provider.id)}
                  />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Provider Details */}
        <Card className="lg:col-span-2">
          {selectedProvider ? (
            <ProviderDetails provider={selectedProvider} />
          ) : (
            <CardContent className="flex items-center justify-center h-[500px] text-muted-foreground">
              Select a provider to view details
            </CardContent>
          )}
        </Card>
      </div>

      {/* Add Provider Dialog */}
      <AddEditProviderDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />
    </div>
  );
}

function ProviderListItem({ 
  provider, 
  isSelected, 
  onSelect 
}: { 
  provider: TrainingProviderExtended; 
  isSelected: boolean; 
  onSelect: () => void;
}) {
  const statusColor = {
    active: 'bg-green-500',
    paused: 'bg-yellow-500',
    hidden: 'bg-orange-500',
    takedown_requested: 'bg-red-500',
    disabled: 'bg-gray-500',
    created: 'bg-blue-500',
  }[provider.lifecycle_status] || 'bg-gray-500';

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-lg transition-colors ${
        isSelected 
          ? 'bg-primary/10 border border-primary/30' 
          : 'hover:bg-muted/50'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium truncate">{provider.display_name}</span>
        <div className={`h-2 w-2 rounded-full ${statusColor}`} />
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Badge variant="outline" className="text-xs">
          {provider.provider_type}
        </Badge>
        {!provider.learning_enabled && (
          <Badge variant="secondary" className="text-xs">
            <BookOpen className="h-3 w-3 mr-1" />
            Learning Off
          </Badge>
        )}
      </div>
    </button>
  );
}

function ProviderDetails({ provider }: { provider: TrainingProviderExtended }) {
  const manageProvider = useManageProvider();
  const dryRunDiscovery = useDryRunDiscovery();
  const runDiscoveryNow = useRunDiscoveryNow();
  const { data: auditLog } = useProviderAuditLog(provider.id, 10);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const handleAction = async (action: string, reason?: string) => {
    try {
      await manageProvider.mutateAsync({
        action: action as any,
        providerId: provider.id,
        reason,
      });
      toast.success(`Provider ${action} successful`);
    } catch (error) {
      toast.error(`Failed to ${action} provider`);
    }
  };

  const handleDryRun = async () => {
    try {
      const result = await dryRunDiscovery.mutateAsync(provider.id);
      toast.success(`Dry-run complete: ${result.sources?.length || 0} sources would be checked`);
    } catch (error) {
      toast.error('Dry-run failed');
    }
  };

  const handleRunNow = async () => {
    try {
      const result = await runDiscoveryNow.mutateAsync({ providerId: provider.id });
      toast.success(`Discovery complete: ${result.itemsAdded || 0} added, ${result.itemsSkipped || 0} skipped`);
    } catch (error) {
      toast.error('Discovery failed');
    }
  };

  return (
    <>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{provider.display_name}</CardTitle>
          <Badge variant={provider.lifecycle_status === 'active' ? 'default' : 'secondary'}>
            {LIFECYCLE_STATUS_LABELS[provider.lifecycle_status]}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick Controls */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
            <Edit className="h-4 w-4 mr-1" /> Edit
          </Button>

          {provider.lifecycle_status === 'active' ? (
            <Button variant="outline" size="sm" onClick={() => handleAction('pause')}>
              <Pause className="h-4 w-4 mr-1" /> Pause
            </Button>
          ) : provider.lifecycle_status === 'paused' ? (
            <Button variant="outline" size="sm" onClick={() => handleAction('resume')}>
              <Play className="h-4 w-4 mr-1" /> Resume
            </Button>
          ) : null}

          {!provider.hidden_from_recommendations ? (
            <Button variant="outline" size="sm" onClick={() => handleAction('hide')}>
              <EyeOff className="h-4 w-4 mr-1" /> Hide
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => handleAction('unhide')}>
              <Eye className="h-4 w-4 mr-1" /> Unhide
            </Button>
          )}

          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRunNow}
            disabled={runDiscoveryNow.isPending}
          >
            <Play className="h-4 w-4 mr-1" /> 
            {runDiscoveryNow.isPending ? 'Running...' : 'Run Now'}
          </Button>

          {provider.lifecycle_status !== 'disabled' && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => handleAction('disable')}
            >
              <XCircle className="h-4 w-4 mr-1" /> Disable
            </Button>
          )}
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Learning Enabled</span>
            </div>
            <Switch
              checked={provider.learning_enabled}
              onCheckedChange={(checked) => 
                handleAction(checked ? 'resume_learning' : 'pause_learning')
              }
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Discovery Enabled</span>
            </div>
            <Switch
              checked={provider.discovery_enabled}
              onCheckedChange={(checked) => 
                handleAction(checked ? 'enable_discovery' : 'disable_discovery')
              }
            />
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Trust Level:</span>
            <span className="ml-2 font-medium">{TRUST_LEVEL_LABELS[provider.trust_level]}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Visibility:</span>
            <span className="ml-2 font-medium">{VISIBILITY_LEVEL_LABELS[provider.visibility_level]}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Manual Review:</span>
            <span className="ml-2">{provider.requires_manual_review ? 'Required' : 'Auto'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Delivery:</span>
            <span className="ml-2">{provider.delivery_modes?.join(', ') || 'N/A'}</span>
          </div>
        </div>

        {/* Recent Audit Log */}
        {auditLog && auditLog.length > 0 && (
          <div>
            <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
              <History className="h-4 w-4" /> Recent Activity
            </h4>
            <div className="space-y-1 text-sm">
              {auditLog.slice(0, 5).map(entry => (
                <div key={entry.id} className="flex items-center justify-between py-1 text-muted-foreground">
                  <span>{entry.action}</span>
                  <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit Provider Dialog */}
      <AddEditProviderDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        provider={provider}
      />
    </>
  );
}

export default ProviderManagementTab;
